import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import * as L from "lucide-react";

export function FocusScreen({ user, go }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [duration, setDuration] = useState(45);
  const [intention, setIntention] = useState("");
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/focus/status");
      setStatus(data);
    } catch { /* non-blocking */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  // While active, tick every second locally; poll server every 20s to reconcile.
  useEffect(() => {
    if (!status?.active) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    const poll = setInterval(() => load(), 20000);
    return () => { clearInterval(timerRef.current); clearInterval(poll); };
     
  }, [status?.active]);

  const remaining = (() => {
    if (!status?.active || !status.ends_at) return 0;
    const ms = new Date(status.ends_at).getTime() - Date.now();
    return Math.max(0, Math.floor(ms / 1000));
  })();
  const mm = Math.floor(remaining / 60);
  const ss = (remaining % 60).toString().padStart(2, "0");

  // Auto-end when timer hits zero.
  useEffect(() => {
    if (status?.active && remaining === 0 && tick > 0) load();
     
  }, [remaining, status?.active]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/integrations/focus/start", {
        duration_min: duration,
        intention: intention.trim() || null,
      });
      toast.success(`Focus on · ${duration} minutes`);
      await load();
    } catch { toast.error("Couldn't start focus session"); }
    finally { setStarting(false); }
  };

  const end = async () => {
    setStarting(true);
    try {
      await api.post("/integrations/focus/end");
      toast("Session ended — nicely done.", { icon: "🌱" });
      await load();
    } catch { toast.error("Couldn't end session"); }
    finally { setStarting(false); }
  };

  if (loading) return <div className="p-5 text-sm text-[var(--rammy-text-muted)]">Loading…</div>;

  return (
    <div className="p-5 space-y-5" data-testid="focus-screen">
      <div>
        <div className="text-2xl font-display">Focus</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
          Choose a quiet block. Rammy silences your email inbox and greets you gently when it&rsquo;s over.
        </p>
      </div>

      {status?.active ? (
        <div className="rounded-3xl bg-[var(--rm-ink-900)] text-white p-6 fade-up" data-testid="focus-active">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">In focus</div>
          <div className="mt-4 font-mono text-6xl tracking-tight text-center tabular-nums" data-testid="focus-timer">
            {mm}:{ss}
          </div>
          {status.intention && (
            <div className="mt-3 text-sm text-white/80 text-center italic" data-testid="focus-intention">
              &ldquo;{status.intention}&rdquo;
            </div>
          )}
          <div className="mt-6 flex justify-center">
            <Button onClick={end} disabled={starting} className="btn-pill h-10 px-5 bg-white/10 text-white hover:bg-white/20" data-testid="focus-end">
              End early
            </Button>
          </div>
          <div className="mt-4 text-[11px] text-white/40 text-center leading-relaxed">
            Emails on hold · No inbox checks · Rammy will send a warm reveal when the timer completes.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5">
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Duration</div>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {[25, 45, 60, 90].map(m => (
                <button
                  key={m}
                  onClick={()=>setDuration(m)}
                  data-testid={`focus-dur-${m}`}
                  data-selected={duration===m ? "true" : "false"}
                  className={`h-11 rounded-xl border text-sm font-medium transition ${
                    duration === m
                      ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/15 text-[var(--rammy-text)]"
                      : "border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[var(--rammy-text-muted)] hover:border-[var(--rammy-cyan)]"
                  }`}
                >
                  {m}m
                </button>
              ))}
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Intention (optional)</div>
            <Input
              value={intention}
              onChange={(e)=>setIntention(e.target.value)}
              placeholder="e.g. Finish investor deck · Write the Rammy pitch"
              className="mt-1 rounded-xl"
              data-testid="focus-intention-input"
            />
          </div>
          <Button onClick={start} disabled={starting} className="btn-pill btn-primary w-full h-12" data-testid="focus-start">
            {starting ? <L.Loader2 size={16} className="animate-spin"/> : <><L.Zap size={16} className="mr-2"/> Start {duration}-min focus</>}
          </Button>
        </div>
      )}
    </div>
  );
}
